import { useCallback, useEffect, useState } from 'react';

/** Reads one stored preference, answering with an empty string when storage is unavailable. */
export function readPreference(key: string): string {
  try {
    return globalThis.localStorage?.getItem(key) ?? '';
  } catch {
    return '';
  }
}

/** Stores one preference; an empty value clears it. */
export function writePreference(key: string, value: string): void {
  try {
    if (value) globalThis.localStorage?.setItem(key, value);
    else globalThis.localStorage?.removeItem(key);
  } catch {
    // A browser with storage disabled still gets the in-memory selection for this session.
  }
}

/**
 * Workspace scope choices belong to one tenant, so the stored value is namespaced by tenant and
 * a tenant switch starts from that tenant's own selection instead of inheriting the previous one.
 */
export function useTenantPreference(tenantId: string | null | undefined, name: string): [string, (value: string) => void] {
  const storageKey = tenantId ? `varza.scope.${tenantId}.${name}` : null;
  const [value, setValue] = useState(() => (storageKey ? readPreference(storageKey) : ''));

  useEffect(() => {
    setValue(storageKey ? readPreference(storageKey) : '');
  }, [storageKey]);

  const update = useCallback((next: string) => {
    setValue(next);
    if (storageKey) writePreference(storageKey, next);
  }, [storageKey]);

  return [value, update];
}
